import _ from "underscore";
import React from "react";
import { createRoot, type Root } from "react-dom/client";
import { LanguageProvider } from "@geotab/zenith";
import type { TSupportedLanguage } from "@geotab/zenith/dist/utils/localization/getSupportedLanguage";
import DriveView from "../../../DriveView";
import { DateSelector } from "../../framework/dateSelector/dateSelector";
import GraphView from "../../hos/graph";
import { Device } from "../../../model/Device";
import { dstCalculator, getStartOfDayUserTimezone, getLocalDaySpan, IReportPeriod, getStartOfDay, dateTimeComparator } from "../../../utils/date";
import { makeUserCache, makeDeviceCache, makeTrailerCache, BaseCache } from "../../../utils/storage/cache";
import { CancelablePromise as Promise } from "../../../utils/cancelablePromise";
import { getCurrentVehicle } from "../../../utils/device";
import Api from "../../../services/api";
import DutyStatusLog, { MALFUNCTION_STATUSES, IAnnotation, NODEVICEID, NOUSERID, AUTO_ANNOTATION_LABEL } from "../../../model/DutyStatusLog";
import { getCurrentPosition } from "../../../utils/location";
import { getServerDate } from "../../../utils/storage/date";
import { IAddressSearch, populateAddressesRedux } from "../../../sync/common";
import dayjs from "../../../utils/dayjs";
import Spinner from "../../../utils/spinner";
import { htmlEscape, createElement, jsonClone, hashToArray } from "../../../utils/vanilla";
import { dispatcher, DispatcherEvents } from "../../../utils/dispatcher";
import { handleApi } from "../../../utils/error";
import { RoadsideCheckLanguageSelector } from "../roadsideCheckLanguageSelector";
import { getShipmentNamesForVehicle, fetchMissingShipments } from "../../../model/Shipments";
import HosProcess from "../../../services/hosProcess";
import { PageLevel } from "../../../store/mobile/types";
import { IOdometerRecord, getOdometer, addLogDistance } from "../../../utils/hosHelpers";
import {
    DeferralStatus, formatUtcDateString, formatVerifyDateTimeToString, getCodeForLog, getCycleInfo,
    getDefaultAvailability, getFirstCertifyLogDict, getOperatingZoneFromRuleSet, getRuleSetForCalculation,
    isInCanada, isVerifyDateAtMidnight, CanadaEldOperatingSystems, getCanadaRegistrationId,
    type THosMalfunction, type THosStatus, type THosOrigin,
    getHoursInWorkShiftFromWorkdaySummary
} from "hos-tools";
import { uuid } from "geotab-uuid";
import pageHTML from "./report.tmpl";
import pageHeader from "./reportHeader.tmpl";
import pageGraph from "./reportGraph.tmpl";
import pageLogs from "./reportLogs.tmpl";
import pageLogsTable from "./reportLogsTable.tmpl";
import canELDPageLogs from "./canELDHosReport/reportLogs.tmpl";
import driverLogsTable from "./canELDHosReport/reportLogsTables/driver.tmpl";
import loginLogoutCertificationMalfunctionLogsTable from "./canELDHosReport/reportLogsTables/loginLogoutCertificationMalfunction.tmpl";
import cycleOperatingZoneDeferralLogsTable from "./canELDHosReport/reportLogsTables/cycleOperatingZoneDeferral.tmpl";
import commentRemarkAnnotationLogsTable from "./canELDHosReport/reportLogsTables/commentRemarkAnnotation.tmpl";
import addHoursNotRecordedLogsTable from "./canELDHosReport/reportLogsTables/addHoursNotRecorded.tmpl";
import powerUpAndShutDownLogsTable from "./canELDHosReport/reportLogsTables/powerUpAndShutDown.tmpl";
import pageOdometer from "./reportOdometer.tmpl";
import pageOdometerTable from "./reportOdometerTable.tmpl";
import * as logger from "../../../utils/logger";
import { getClonedAnnotationsForUser } from "../../../utils/storeHelpers";
import { isIphone } from "../../../utils/native/nativeHelpers";
import { getEldProviderName } from "../../../utils/helpers";
import type { ITrailer } from "../../../model/Trailer";
import { FeatureFlagName } from "../../../utils/featureFlagName";
import type { IFeatureFlagsService } from "../../../services/featureFlags";
import { STATUSES_TO_OMIT_STATUSDATA } from "../../../services/createLogWithOmittedDataWarningService";

export interface IHosReportLog {
    id: string;
    driver: IIdEntity;
    origin: THosOrigin;
    vehicleName?: string;
    dateTime: Date;
    verifyDateTime?: string;
    latitude?: number;
    longitude?: number;
    distanceSinceLastValidCoordinate?: number;
    distance?: number;
    status: THosStatus;
    malfunction?: THosMalfunction;
    type?: THosStatus | string;
    location?: string;
    odometer?: number;
    engineHours?: number;
    annotationString?: string;
    annotations?: IAnnotation[];
    sequence?: string;
    sequenceNumber?: string;
    state?: string;
    distanceSinceLastPowerUp?: number;
    engineHoursSinceLastPowerUp?: number;
    isCreatedWhileDeviceNotConnected?: boolean;
    deviceId?: string;
    deferralStatus?: string;
    deferralMinutes?: number;
    event: string;
    additionalInfo: string;
}

export interface IDutyStatusLogForReport extends DutyStatusLog {
    distanceSinceLastPowerUp?: number;
    engineHoursSinceLastPowerUp?: number;
    isCreatedWhileDeviceNotConnected?: boolean;
}

interface IHeader {
    id: string;
    label?: string;
    value?: string;
    translate?: boolean;
    hasHtml?: boolean;
}

interface IDistanceDisplay {
    device?: Device;
    distance: number;
}

enum TableType {
    Driver,
    LoginLogoutCertificationMalfunction,
    CycleOperatingZoneDeferral,
    CommentRemarkAnnotation,
    AddHoursNotRecorded,
    PowerUpAndShutDown,
    Unassigned
}

enum EventRecordOriginParameterCoding {
    Automatic = 1,
    Manual = 2,
    OtherUser = 3,
    Unassigned = 4
}

type DutyStatusLogOrigin = keyof typeof EventRecordOriginParameterCoding;

enum EventRecordStatusParameterCoding {
    Active = 1,
    Inactive = 2,
    Requested = 3,
    Rejected = 4
}

type DutyStatusLogState = keyof typeof EventRecordStatusParameterCoding;

interface IReportPeriodLogs {
    driverLogs: IHosReportLog[];
    unassignedLogs: IHosReportLog[];
    malfunctionLogs: IHosReportLog[];
    loginLogoutCertificationMalfunctionLogs?: IHosReportLog[];
    cycleOperatingZoneDeferralLogs?: IHosReportLog[];
    commentRemarkAnnotationLogs?: IHosReportLog[];
    addHoursNotRecordedLogs?: IHosReportLog[];
    powerUpAndShutDownLogs?: IHosReportLog[];
}

interface IEldProviderInfo {
    id: string;
    provider: string;
}

enum MalfunctionStatuses {
    "PowerCompliance" = "Power",
    "EngineSyncCompliance" = "Engine synchronization",
    "TimingCompliance" = "Timing",
    "PositioningCompliance" = "Positioning",
    "DataRecordingCompliance" = "Data recording",
    "DataTransferCompliance" = "Data transfer",
    "OtherCompliance" = "Other",
    "MissingElementCompliance" = "Missing element",
    "UnidentifiedDrivingCompliance" = "Unidentified driving"
}

export default class HosReportView extends DriveView<Backbone.Model> {

    protected strings = [
        "Rotate your device or swipe the table below for more details",
        "Yes",
        "No",
        "hours",
        "h",
        "Int Location",
        "No carrier number",
        "No vehicle",
        "Cycle 1",
        "Cycle 2",
        "{0} days",
        "Operating Zone 1 (South of latitude 60°N in Canada)",
        "Operating Zone 2 (North of latitude 60°N in Canada)",
        "Operating Zone 3 (United States)",
        "Off-duty deferral",
        "Day one",
        "Day two",
        "Power",
        "Engine synchronization",
        "Timing",
        "Positioning",
        "Data recording",
        "Data transfer",
        "Other",
        "Missing element",
        "Unidentified driving",
        "Timezone",
        "UTC",
        "Code",
        "Malfunction (Cleared)",
        "Diagnostic (Cleared)",
        "Malfunction (Detected)",
        "Diagnostic (Detected)",
        "Date of certified RODS",
        "Certification of RODS",
        "Re-certification of RODS"
    ];
    private readonly template = _.template(pageHTML);
    private readonly templateHeader = _.template(pageHeader);
    private readonly templateGraph = _.template(pageGraph);
    private readonly templateLogs = _.template(pageLogs);
    private readonly canELDTemplateLogs = _.template(canELDPageLogs);
    private readonly templateLogsTable = _.template(pageLogsTable);
    private readonly templateDriverLogsTable = _.template(driverLogsTable);
    private readonly templateLoginLogoutCertificationMalfunctionLogsTable = _.template(loginLogoutCertificationMalfunctionLogsTable);
    private readonly templateCycleOperatingZoneDeferralLogsTable = _.template(cycleOperatingZoneDeferralLogsTable);
    private readonly templateCommentRemarkAnnotationLogsTable = _.template(commentRemarkAnnotationLogsTable);
    private readonly templateAddHoursNotRecordedLogsTable = _.template(addHoursNotRecordedLogsTable);
    private readonly templatePowerUpAndShutDownLogsTable = _.template(powerUpAndShutDownLogsTable);
    private readonly templateOdometer = _.template(pageOdometer);
    private readonly templateOdometerTable = _.template(pageOdometerTable);
    private readonly api: Api;
    private readonly deviceCache: BaseCache<Device | IDevice>;
    private readonly userCache: BaseCache<IUser>;
    private readonly trailerCache: BaseCache<ITrailer>;
    private readonly hosUtils: HosProcess;
    private readonly spinner = new Spinner({
        top: "auto",
        left: "auto"
    });
    private readonly isCanadaEld: boolean;

    // The date/time the driver first viewed this report page in UTC for authentication value calculations
    private readonly currentDateTimeOfPrintoutDisplay = getServerDate();
    private readonly featureFlagsService: IFeatureFlagsService;
    private readonly currentUser: DeepReadonly<IUser> | null = null;

    private graphView: GraphView | undefined;
    private dateSelectorReactRoot: Root | null = null;
    private readonly languageSelector: RoadsideCheckLanguageSelector;
    private reportDate: Date;
    private reportPeriod: IReportPeriod;
    private language: string;
    private hasMissingData: boolean;
    private authorityLogs: DutyStatusLog[];
    private standardLogsCollection: DutyStatusLog[];
    private reportPeriodStandardLogs: DutyStatusLog[];
    private reportPeriodLogs: IReportPeriodLogs;
    private driverLogsCollection: IDutyStatusLogForReport[];
    private unassignedLogsCollection: IDutyStatusLogForReport[];
    private malfunctionLogsCollection: DutyStatusLog[];
    private loginLogoutCertificationMalfunctionLogsCollection: DutyStatusLog[];
    private cycleOperatingZoneDeferralLogsCollection: DutyStatusLog[];
    private commentRemarkAnnotationLogsCollection: DutyStatusLog[];
    private addHoursNotRecordedLogsCollection: DutyStatusLog[];
    private powerUpAndShutDownLogsCollection: DutyStatusLog[];
    private currentAddress: string;
    private firstCertifyLogDict: IIdDictionary<IDutyStatusLogForReport> = {};

    private renderPromise: Promise<void>;
    private coDriverPromise: Promise<IUser[] | null>;
    private geoLocationPromise: Promise<string>;
    private loadLogsPromise: Promise<void>;
    private loadOdometerPromise: Promise<void>;
    private eldAuthenticationValue = "";

    constructor(options) {
        super(options, {
            title: "Compliance report",
            activeSection: "inspection",
            pageLevel: PageLevel.ASSET_SPECIFIC_PAGE
        });

        this.hosUtils = this.app.hosUtils;
        this.languageSelector = new RoadsideCheckLanguageSelector({
            app: this.app,
            getTranslator: () => this.translator,
            getElement: () => this.el,
            getHosRuleSetIdShortened: () => this.hosUtils.getLatestHosRuleSetIdShortened(),
            onLanguageApplied: language => this.switchLanguage(language)
        });
        this.api = this.app.api;
        this.deviceCache = makeDeviceCache(this.app.store);
        this.userCache = makeUserCache(this.app.store);
        this.trailerCache = makeTrailerCache(this.app.store);
        this.currentUser = this.app.userService.getCurrentUser();

        this.isCanadaEld = this.options?.state.isCanadaEld;
        // report date converted according to timezone from user settings
        this.reportDate = getStartOfDayUserTimezone(getServerDate());
        this.language = this.app.translationService.getLanguage() || "en";
        this.featureFlagsService = this.app.featureFlagService;
        this.listenTo(dispatcher, DispatcherEvents.SyncHos, this.loadData.bind(this));

        this.graphView = new GraphView({
            app: this.app,
            language: this.language
        });
    }

    /* eslint-disable @typescript-eslint/unbound-method */
    events() {
        return {
            "click #roadside-check-language-selector": this.onLanguageSelectorClick
        } as Backbone.EventsHash;
    }
    /* eslint-enable @typescript-eslint/unbound-method */

    render(newLogs = true, newReportPeriod = true) {
        const languageViewModel = this.languageSelector.getViewModel();

        void Promise.all([
            this.app.localizerService.initUserUnits(this.translator, this.language),
            this.translate(this.language),
            this.display(this.template(languageViewModel), this.language)
        ]).then(() => {
            this.app.setFullWidth(true);
            this.renderReportNavigation();
            void this.loadData(newLogs, newReportPeriod);
        });

        return this;
    }

    remove() {
        this.languageSelector.remove();
        this.app.setFullWidth(false);
        this.cancelPromises();
        if (this.dateSelectorReactRoot) {
            this.dateSelectorReactRoot.unmount();
            this.dateSelectorReactRoot = null;
        }
        this.graphView && this.graphView.remove();
        super.remove();
        this.graphView = undefined;
        return this;
    }

    switchLanguage(language: string): void {
        if (language !== this.language) {
            this.language = language;
            this.render(false, false);
            this.graphView && this.graphView.switchLanguage(language);
        }
    }

    onLanguageSelectorClick() {
        this.languageSelector.handleClick();
    }

    requestEldAuthenticationValue(lastDeviceId: string | undefined) {
        if (!this.isCanadaEld || this.eldAuthenticationValue !== "") {
            return;
        }

        let credentials = this.app.api.getCurrentCredentials();
        let database = credentials ? credentials.credentials.database : "";

        return this.api.callPromise("GetEldAuthenticationValue", {
            providerName: getEldProviderName(database, window.whitelabel, this.isCanadaEld),
            driverId: this.currentUser?.id,
            deviceId: lastDeviceId,
            reportDateTime: this.currentDateTimeOfPrintoutDisplay.toISOString(),
            os: isIphone() ? CanadaEldOperatingSystems.IOS : CanadaEldOperatingSystems.Android
        })
        .then(result => {
            this.eldAuthenticationValue = result;
            return result;
        }).catch(error => {
            logger.error(logger.Source.API, `GetEldAuthenticationValue: ${ error?.message }`);
        });
    }

    private loadData(newLogs = true, newReportPeriod = true) {
        this.cancelPromises();

        let reportDate = this.reportDate;
        this.reportPeriod = getLocalDaySpan(reportDate);

        let spinnerContainer = this.el.querySelector("#report_dailyReport") as HTMLElement;
        if (spinnerContainer) {
            spinnerContainer.classList.add("panel_padding");
            this.spinner.spin(this.el.querySelector("#report_dailyReport"));
        }

        newLogs && this.refreshLogs();
        newReportPeriod && this.graphView && this.graphView.updateDisplayedDate(this.reportDate);

        this.renderPromise = (newReportPeriod ? this.filterLogsForReportPeriod(this.reportPeriod) : Promise.resolve())
            .then(() => this.getHeaderData(this.reportPeriodLogs.unassignedLogs || []))
            .then(header => {
                if (header) {
                    let thisHeader = this.changeHeaderMalfunctionsData(header, this.reportPeriodLogs.malfunctionLogs);
                    this.renderHeaderData(thisHeader, this.hasMissingData);
                }

                this.isCanadaEld ? void this.renderLogsCanadaELD(this.reportPeriodLogs) : void this.renderLogs(this.reportPeriodLogs);
                void this.renderOdometer();
                this.renderGraph();
                this.spinner.stop();
                spinnerContainer && spinnerContainer.classList.remove("panel_padding");
            })
            .catch(handleApi);
        return this.renderPromise;
    }

    private trimMalfunctionDiagnosticLogs(malfunctionDiagnosticLogs: IHosReportLog[], logCollection: IHosReportLog[]) {
        let diagnosticCount = 0;
        let malfunctionCount = 0;
        let malfunctionsDiagnosticsToSave: IHosReportLog[] = [];
        // UnidentifiedDrivingCompliance diagnostics are stored for 14 days in Canada but 7 days in US
        let filterStartDateMs = this.getRetentionStartDateMsForUnidentifiedDriving();
        for (let i = malfunctionDiagnosticLogs.length - 1; i >= 0; i--) {
            if (diagnosticCount === 10 && malfunctionCount === 10) {
                break;
            }

            let log = malfunctionDiagnosticLogs[i];
            if (log.malfunction) {
                if (log.malfunction.indexOf("Diagnostic") > -1) {
                    // Should not display UnidentifiedDrivingDiagnostics that are older than 14 days in Canada and 7 days in US
                    if (log.status === "UnidentifiedDrivingCompliance" && new Date(log.dateTime).getTime() < filterStartDateMs) {
                        continue;
                    }
                    if (diagnosticCount < 10 && this.shouldLogBeDisplayed(logCollection, log)) {
                        diagnosticCount++;
                        malfunctionsDiagnosticsToSave.push(log);
                    }
                } else if (log.malfunction.indexOf("Malfunction") > -1) {
                    if (malfunctionCount < 10 && this.shouldLogBeDisplayed(logCollection, log)) {
                        malfunctionCount++;
                        malfunctionsDiagnosticsToSave.push(log);
                    }
                }
            }
        }

        return malfunctionsDiagnosticsToSave.reverse();
    }

    private getRetentionStartDateMsForUnidentifiedDriving() {
        return this.hosUtils.getUnidentifiedDrivingRetentionStartDate(this.isCanadaEld).getTime();
    }

    private updateOdometerAndEngineHoursIfLogCreatedWhileDeviceNotConnected(
        log: IDutyStatusLogForReport,
        currentVehicle: IIdEntity | null,
        isDeviceCommunicating: boolean | undefined,
        lastDeviceCommunicationDateTime: string | undefined
    ) {
        if (log.state === "Requested"
            || log.driver.id !== this.currentUser?.id
            || STATUSES_TO_OMIT_STATUSDATA.indexOf(log.status) === -1
            || log.isAutomaticLog()
        ) {
            return;
        }

        if (log.device.id === NODEVICEID) {
            log.isCreatedWhileDeviceNotConnected = true;
            return;
        }

        if (log.device.id === currentVehicle?.id
            && isDeviceCommunicating === false
            && (!lastDeviceCommunicationDateTime || new Date(lastDeviceCommunicationDateTime).getTime() < new Date(log.dateTime).getTime())
        ) {
            log.isCreatedWhileDeviceNotConnected = true;
            if (log.distance !== undefined) {
                log.distance = 0;
            }
        }
    }

    private shouldLogBeDisplayed(logCollection: IHosReportLog[], malfunctionLog: IHosReportLog) {
        if (!malfunctionLog.malfunction) {
            return false;
        }

        // These are normally filtered in the malfunction sync by workday
        // Clear logs and positioningCompliance malfunction logs are kept regardless of workdate, need to verify here if they should be displayed or not
        if (malfunctionLog.status !== "PositioningCompliance" && malfunctionLog.malfunction.indexOf("Clear") === -1) {
            return true;
        }

        let date = malfunctionLog.dateTime.toISOString().split("T")[0];
        if (!date) {
            return true;
        }

        return logCollection.some(log => {
            if (log.deviceId === malfunctionLog.deviceId) {
                let logDate = log.dateTime.toISOString().split("T")[0];
                return logDate === date;
            }
            return false;
        });
    }

    private getAllVehiclesUsedInRange() {
        let vehicleDistanceDictionary: IIdDictionary<IDistanceDisplay> = {};
        let collection = this.app.dutyStatusLogService.getLogsByDate(this.app.dutyStatusLogService.getHOSLogs(true, undefined, false, true), this.reportPeriod, true);
        collection.forEach(log => {
            let deviceId = log.getDeviceId();
            if (deviceId && deviceId !== "NoDeviceId" && !vehicleDistanceDictionary.hasOwnProperty(deviceId)) {
                vehicleDistanceDictionary[deviceId] = { distance: 0 };
            }
        });
        return vehicleDistanceDictionary;
    }

    private getCertifyLogsOutsideOfRange(collection: DutyStatusLog[], reportPeriod: IReportPeriod) {
        let reportLocalStartDateString = formatUtcDateString(dstCalculator.toLocalDateTime(reportPeriod.fromDate));
        let additionalCertifyLogs: DutyStatusLog[] = [];

        collection.forEach(log => {
            if (log.isCertifyLog() && log.verifyDateTime
                && reportLocalStartDateString === formatVerifyDateTimeToString(log.verifyDateTime, dstCalculator.toLocalDateTime)
                && reportLocalStartDateString !== formatUtcDateString(dstCalculator.toLocalDateTime(log.dateTime))
            ) {
                additionalCertifyLogs.push(log);
            }
        });

        return additionalCertifyLogs;
    }

    private pushAnnotationToCollectionIfExists(log: DutyStatusLog) {
        // [For each Annotation or Comment to driver’s ELD records]:
        if (log.hasAnnotations() || log.isLoginLogoffLog()) {
            this.commentRemarkAnnotationLogsCollection.push(log);
        }
    }

    private refreshLogs () {
        let collection: DutyStatusLog[] = this.app.dutyStatusLogService.getHOSLogs(true, undefined, false, true);

        this.authorityLogs = [];
        this.malfunctionLogsCollection = [];
        this.unassignedLogsCollection = [];
        this.standardLogsCollection = [];
        this.driverLogsCollection = [];
        this.cycleOperatingZoneDeferralLogsCollection = [];
        this.loginLogoutCertificationMalfunctionLogsCollection = [];
        this.commentRemarkAnnotationLogsCollection = [];
        this.addHoursNotRecordedLogsCollection = [];
        this.powerUpAndShutDownLogsCollection = [];

        if (!this.isCanadaEld) {
            collection = collection.filter(log => !(log.isMalfunctionLog() && log.isSystemClear()));
        }
        let filterStartDateMs = this.getRetentionStartDateMsForUnidentifiedDriving();

        const currentVehicle = getCurrentVehicle();
        const isDeviceCommunicating = this.app.store.getState().mobile.deviceCommunicating;
        const lastDeviceCommunicationDateTime = this.app.store.getState().mobile.lastDeviceCommunicationDateTime;

        collection.forEach(log => {
            if (log.isAuthority()) {
                this.authorityLogs.push(log);
                return;
            }

            if (this.isCanadaEld) {
                this.updateOdometerAndEngineHoursIfLogCreatedWhileDeviceNotConnected(
                    log,
                    currentVehicle,
                    isDeviceCommunicating,
                    lastDeviceCommunicationDateTime
                );

                if (log.isPlaceholder()) {
                    this.standardLogsCollection.push(log);
                    return;
                }

                if (!log.isControlLog()) {
                    this.standardLogsCollection.push(log);
                }

                this.pushAnnotationToCollectionIfExists(log);

                // [For Each Change in Operating Zone Events specified in provision 4.5.1.10 of this Standard]:
                // [For Each Off-Duty Time Deferral Events specified in provision 4.5.1.8 of this Standard]:
                if (log.isCycleOrZoneChangeLog() || log.status === "ExemptionOffDutyDeferral") {
                    this.cycleOperatingZoneDeferralLogsCollection.push(log);
                    return;
                }

                if (log.isMalfunctionLog()) {
                    // Should not display UnidentifiedDrivingDiagnostics that are older than 14 days in Canada and 7 days in US
                    if (log.isPositioningComplianceDiagnosticLog() || (log.status === "UnidentifiedDrivingCompliance" && new Date(log.dateTime).getTime() < filterStartDateMs)) {
                        return;
                    }

                    // [For Each Malfunctions and Data Diagnostic Events specified in provision 4.5.1.7 of this Standard]:
                    this.malfunctionLogsCollection.push(log);
                    return;
                }

                // [For Each Driver’s Certification of RODS Events specified in provision 4.5.1.4 of this Standard]:
                // [For Each ELD Login/Logout Events specified in provision 4.5.1.5 of this Standard]:
                if (log.isCertifyLog() || log.isLoginLogoffLog()) {
                    this.loginLogoutCertificationMalfunctionLogsCollection.push(log);
                    return;
                }

                if (log.isUnidentified()) {
                    this.unassignedLogsCollection.push(log);
                    return;
                }

                // [For Each CMV Engine Power up / Shut Down Events specified in provision 4.5.1.6 of this Standard]
                if (log.isEngineLog()) {
                    this.powerUpAndShutDownLogsCollection.push(log);
                    return;
                }

                // [For each Additional Hours Not Recorded Events specified in provision 4.5.1.11 of this Standard]:
                /* COMING SOON in MYG-14028 */
                /* https://jira.geotab.com/browse/MYG-14028 */
            } else {
                if (log.isMalfunctionLog()) {
                    // Should not display UnidentifiedDrivingDiagnostics that are older than 7 days in US
                    if (log.isPositioningComplianceDiagnosticLog() || (log.status === "UnidentifiedDrivingCompliance" && new Date(log.dateTime).getTime() < filterStartDateMs)) {
                        return;
                    }

                    this.malfunctionLogsCollection.push(log);
                    return;
                }

                if (log.isUnidentified()) {
                    this.unassignedLogsCollection.push(log);
                    return;
                }

                if (log.isPlaceholder()) {
                    this.standardLogsCollection.push(log);
                    return;
                }

                if (!log.isControlLog()) {
                    this.standardLogsCollection.push(log);
                }
            }

            //  [For each Change of Duty Status and Intermediate Logs Events specified in provisions 4.5.1.1 and 4.5.1.2 of this Standard]:
            if (log.isStandardStatusDriverLogForReport(this.currentUser)) {
                this.driverLogsCollection.push(log);
            }
        });

        this.populateAccumulated(this.driverLogsCollection, this.unassignedLogsCollection, this.powerUpAndShutDownLogsCollection);
        this.firstCertifyLogDict = getFirstCertifyLogDict(this.loginLogoutCertificationMalfunctionLogsCollection, dstCalculator.toLocalDateTime);
        this.graphView && this.graphView.loadLogs();
    }

    private filterLogsForReportPeriod (reportPeriod: IReportPeriod): Promise {
        this.reportPeriodStandardLogs = this.app.dutyStatusLogService.getLogsByDate(this.app.dutyStatusLogService.clone(this.standardLogsCollection), reportPeriod, true, true);
        let reportLogsPromise: Promise<IHosReportLog[]>[] = [
            this.organizeForReport(this.driverLogsCollection, reportPeriod),
            this.organizeForReport(this.unassignedLogsCollection, reportPeriod),
            this.organizeForReport(this.malfunctionLogsCollection, reportPeriod)
        ];

        if (this.isCanadaEld) {
            let additionalCertifyLogs = this.getCertifyLogsOutsideOfRange(this.loginLogoutCertificationMalfunctionLogsCollection, reportPeriod);

            reportLogsPromise = reportLogsPromise.concat([
                this.organizeForReport(this.loginLogoutCertificationMalfunctionLogsCollection, reportPeriod, additionalCertifyLogs),
                this.organizeForReport(this.cycleOperatingZoneDeferralLogsCollection, reportPeriod),
                this.organizeForReport(this.commentRemarkAnnotationLogsCollection, reportPeriod),
                this.organizeForReport(this.addHoursNotRecordedLogsCollection, reportPeriod),
                this.organizeForReport(this.powerUpAndShutDownLogsCollection, reportPeriod)
            ]);
        }

        return Promise.all(reportLogsPromise).then(reportLogs => {
            let malfunctionLogsTrimmed = this.trimMalfunctionDiagnosticLogs(reportLogs[2], reportLogs[0]);

            this.reportPeriodLogs = {
                driverLogs: reportLogs[0],
                unassignedLogs: reportLogs[1],
                malfunctionLogs: malfunctionLogsTrimmed
            };

            if (this.isCanadaEld) {
                this.reportPeriodLogs.loginLogoutCertificationMalfunctionLogs = reportLogs[3].concat(malfunctionLogsTrimmed).sort(dateTimeComparator());
                this.reportPeriodLogs.cycleOperatingZoneDeferralLogs = reportLogs[4];
                this.reportPeriodLogs.commentRemarkAnnotationLogs = reportLogs[5];
                this.reportPeriodLogs.addHoursNotRecordedLogs = reportLogs[6];
                this.reportPeriodLogs.powerUpAndShutDownLogs = reportLogs[7];
            }

            return;
        });
    }

    private formatSpecialEvent (log: DutyStatusLog) {
        if (log.isOffDutyDeferral() && log.deferralStatus && log.deferralMinutes) {
            return `${ this.getString("Off-duty deferral") }: ` + this.getString(log.deferralStatus === "DayOne" ? "Day one" : "Day two") +
                ` (${ (`0${ Math.floor(log.deferralMinutes / 60) }`.slice(-2)) }:${ (`0${ log.deferralMinutes % 60 }`.slice(-2)) })`;
        }

        if (log.isCycleChangeLog()) {
            switch (log.status) {
                case "CanadaCycleOne":
                    return `${ this.getString("Cycle 1") } (${ this.getString("{0} days").replace("{0}", "7") })`;
                case "CanadaCycleTwo":
                    return `${ this.getString("Cycle 2") } (${ this.getString("{0} days").replace("{0}", "14") })`;
                default:
                    return "";
            }
        }

        if (log.isCertifyLog() && log.verifyDateTime && this.isCanadaEld) {
                let dateString = formatVerifyDateTimeToString(log.verifyDateTime, dstCalculator.toLocalDateTime);
                if (dateString && (!this.firstCertifyLogDict.hasOwnProperty(dateString) || this.firstCertifyLogDict[dateString].id === log.id)) {
                    return this.getString("Certification of RODS");
                }
                return this.getString("Re-certification of RODS");
        }

        if (log.isOperatingZoneChange()) {
            switch (log.status) {
                case "OperatingZoneCanadaSouthOf60":
                    return this.getString("Operating Zone 1 (South of latitude 60°N in Canada)");
                case "OperatingZoneCanadaNorthOf60":
                    return this.getString("Operating Zone 2 (North of latitude 60°N in Canada)");
                case "OperatingZoneAmerica":
                    return this.getString("Operating Zone 3 (United States)");
                default:
                    return "";
            }
        }

        if (log.isMalfunctionLog()) {
            let malfunctionString = "";
            let isClear = log.isSystemClear();

            if (log.isDiagnostic()) {
                malfunctionString += this.getString(`Diagnostic (${ isClear ? "Cleared" : "Detected" })`);
            } else {
                malfunctionString += this.getString(`Malfunction (${ isClear ? "Cleared" : "Detected" })`);
            }

            return malfunctionString;
        }

        return log.status;
    }

    private formatVerifyDateTime (dateString?: string) {
        if (!dateString) {
            return "";
        }

        // In 9.0+, verifyDateTime is always at midnight UTC on the appropriate date. In 8.0 and under, verifyDateTime is adjusted to midnight of the user's timezone. Both cases need to be handled.
        if (isVerifyDateAtMidnight(dateString)) {
            let utcDate = dateString.split("T")[0].split("-");
            return `${ utcDate[1] }-${ utcDate[2] }-${ utcDate[0].slice(-2) }`;
        }
        return dayjs(dstCalculator.toLocalDateTime(dateString)).format("MM-DD-YY");
    }

    private formatAdditionalInfo (log: DutyStatusLog, timezoneOffset: string) {
        if (log.isCertifyLog()) {
            return `${ this.getString("Timezone") }: ${ timezoneOffset } ${ this.getString("UTC") }
                <br><br>${ this.getString("Date of certified RODS") }: ${ this.formatVerifyDateTime(log.verifyDateTime) }`;
        }

        if (log.isMalfunctionLog()) {
            return `${ this.getString("Code") }: ${ getCodeForLog(log.malfunction, log.status) } (${ this.getString(MalfunctionStatuses[log.status]) })`;
        }

        return "";
    }

    private organizeForReport (unsortedLogs: DutyStatusLog[], reportPeriod: IReportPeriod, additionalLogs?: DutyStatusLog[]): Promise<IHosReportLog[]> {
        if (unsortedLogs.length === 0) {
            return Promise.resolve([]);
        }

        let logs: IDutyStatusLogForReport[] = this.app.dutyStatusLogService.getLogsByDate(unsortedLogs, reportPeriod);
        if (additionalLogs) {
            logs.push(...additionalLogs);
        }

        const apiUserId = this.api.getUser()?.id;
        const annotationList = getClonedAnnotationsForUser(this.app.store, apiUserId);

        let timeZoneOffset = this.getTimeZoneOffset(this.currentUser?.timeZoneId);
        let getVehicleName = deviceId => {
            if (deviceId) {
                if (deviceId === "NoDeviceId") {
                    return this.getString("No vehicle");
                }
                let vehicle = this.deviceCache.get({ id: deviceId })[0];
                return vehicle ? htmlEscape(vehicle.name) : "";
            } else {
                return "";
            }
        };

        return Promise.all(logs.map(log => this.app.dutyStatusLogService.getAnnotations(log, this.translator)))
            .then(annotations => {
                const dateComparer = dateTimeComparator();
                let annotationUserIds = {};
                let logsList: DutyStatusLog[] = logs.map((logModel, i) => {
                    let log: IDutyStatusLogForReport = new DutyStatusLog(jsonClone(logModel));
                    log.distanceSinceLastPowerUp = logModel.distanceSinceLastPowerUp;
                    log.engineHoursSinceLastPowerUp = logModel.engineHoursSinceLastPowerUp;
                    log.isCreatedWhileDeviceNotConnected = logModel.isCreatedWhileDeviceNotConnected;
                    log.annotations = annotations[i].reduce((annotationsArr, currentAnnotation) => {
                        if (currentAnnotation.hasOwnProperty("id")) {
                            let annotation = annotationList[(currentAnnotation as IAnnotation).id] || currentAnnotation;
                            // Flag genuine NoUser-authored annotations. Synthetic comments (comment branch) share NOUSERID but must stay unflagged.
                            const isAutoAnnotation = annotation.driver === NOUSERID || annotation.driver?.id === NOUSERID;
                            annotationsArr.push(isAutoAnnotation ? { ...annotation, isAutoAnnotation: true } : annotation);

                            if (!isAutoAnnotation && annotation.driver && annotation.driver !== NOUSERID && annotation.driver.id) {
                                annotationUserIds[annotation.driver.id] = true;
                            }
                        } else if (currentAnnotation.hasOwnProperty("comment")) {
                            let convertedAnnotation: IAnnotation = {
                                dateTime: log.dateTime,
                                id: uuid(),
                                comment: currentAnnotation.comment,
                                driver: NOUSERID
                            };
                            annotationsArr.push(convertedAnnotation);
                        }

                        return annotationsArr;
                    }, [] as IAnnotation[]);
                    log.annotations.sort(dateComparer);
                    return log;
                });
                return this.userCache.getOrFetch(this.api, Object.keys(annotationUserIds).map(id => ({ id })))
                    .then(() => this.populateLogs(this.api, logsList))
                    .catch(() => this.populateLogs(this.api, logsList));
            })
            .then(populatedLogs => populatedLogs.map((log: IDutyStatusLogForReport): IHosReportLog => {
                let logAnnotations = "";
                let getSequenceHexString = function (sequence) {
                    if (!sequence) {
                        return "";
                    }

                    let intValue = parseInt(sequence, 16) % 0xFFFF;
                    let hexString = intValue.toString(16);
                    while (hexString.length < 4) {
                        hexString = `0${ hexString }`;
                    }

                    return hexString.toUpperCase();
                };

                log.annotations?.forEach(annotation => {
                    let comment = annotation ? annotation.comment : null;

                    if (comment) {
                        logAnnotations += `${ htmlEscape(comment) }<br>`;
                    }
                });

                return {
                    id: log.id,
                    driver: log.driver,
                    origin: log.origin,
                    dateTime: new Date(log.dateTime),
                    verifyDateTime: log.verifyDateTime,
                    latitude: log.location?.location?.y,
                    longitude: log.location?.location?.x,
                    distanceSinceLastValidCoordinate: log.distanceSinceValidCoordinates,
                    distance: log.distance,
                    location: log.location?.address?.formattedAddress,
                    odometer: log.odometer,
                    engineHours: log.engineHours,
                    status: log.status,
                    state: log.state,
                    malfunction: log.malfunction,
                    type: MALFUNCTION_STATUSES.indexOf(log.status) > -1 ? `${ log.status } ${ log.malfunction }` : log.status,
                    vehicleName: getVehicleName(log.device.id),
                    annotationString: logAnnotations,
                    annotations: log.annotations,
                    sequence: getSequenceHexString(log.sequence),
                    distanceSinceLastPowerUp: log.distanceSinceLastPowerUp || 0,
                    engineHoursSinceLastPowerUp: log.engineHoursSinceLastPowerUp || 0,
                    isCreatedWhileDeviceNotConnected: log.isCreatedWhileDeviceNotConnected || false,
                    deviceId: log.device?.id,
                    deferralMinutes: log.deferralMinutes,
                    deferralStatus: log.deferralStatus,
                    event: this.formatSpecialEvent(log),
                    additionalInfo: this.formatAdditionalInfo(log, timeZoneOffset)
                };
            }))
            .catch(error => {
                logger.log(logger.Source.UI, error.message);
                this.hasMissingData = true;
                return [];
            });
    }

    private populateAccumulated(standardLogs: IDutyStatusLogForReport[], unassignedLogs: IDutyStatusLogForReport[], engineLogs: DutyStatusLog[]) {
        if (!(engineLogs && engineLogs.length > 0)) {
            return;
        }

        let standardLogsWithEngineLogs = standardLogs.concat(unassignedLogs).concat(engineLogs);
        standardLogsWithEngineLogs?.sort(dateTimeComparator());

        let lastIgnitionLogDict: IIdDictionary<DutyStatusLog | null> = {};
        let getDistanceSincePowerUp = (log: IDutyStatusLogForReport, enginePowerUpLog: DutyStatusLog | null) => {
            return log.odometer !== undefined && enginePowerUpLog?.odometer !== undefined ? log.odometer - enginePowerUpLog.odometer : 0;
        };
        let getEngineHoursSincePowerUp = (log: IDutyStatusLogForReport, enginePowerUpLog: DutyStatusLog | null) => {
            return log.engineHours !== undefined && enginePowerUpLog?.engineHours !== undefined ? log.engineHours - enginePowerUpLog.engineHours : 0;
        };

        standardLogsWithEngineLogs.forEach(log => {
            let deviceId = log.getDeviceId();
            if (!deviceId) {
                return;
            }

            if (log.isEngineLog()) {
                if (log.isEnginePowerUpLog()) {
                    lastIgnitionLogDict[deviceId] = log;
                } else {
                    lastIgnitionLogDict[deviceId] = null;
                }
                return;
            }

            log.distanceSinceLastPowerUp = getDistanceSincePowerUp(log, lastIgnitionLogDict[deviceId]);
            log.engineHoursSinceLastPowerUp = getEngineHoursSincePowerUp(log, lastIgnitionLogDict[deviceId]);
        });
    }

    private changeHeaderMalfunctionsData(headerData, malfunctionLogs) {
        let malfunctions: DutyStatusLog[] = malfunctionLogs.filter((malf: DutyStatusLog) => malf.malfunction && malf.malfunction.match(/Malfunction$/));
        let diagnostics: DutyStatusLog[] = malfunctionLogs.filter((malf: DutyStatusLog) => malf.malfunction && malf.malfunction.match(/Diagnostic$/));
        let latestDiagnostic = diagnostics.length > 0 ? diagnostics[diagnostics.length - 1] : null;
        let latestMalfunction = malfunctions.length > 0 ? malfunctions[malfunctions.length - 1] : null;

        for (let i = 0; i < headerData.length; i++) {
            let entity = headerData[i];
            if (entity.id === "eld-status-malfunctions") {
                entity.value = malfunctions.length > 0 ? this.getString("Yes") : this.getString("No");
            } else if (entity.id === "eld-status-diagnostics") {
                entity.value = diagnostics.length > 0 ? this.getString("Yes") : this.getString("No");
            } else if (entity.id === "eld-status-data-diagnostic-status") {
                let code = latestDiagnostic ? getCodeForLog(latestDiagnostic.malfunction, latestDiagnostic.status) : null;
                entity.value = code ? `1 (Code ${ code })` : "0";
            } else if (entity.id === "eld-status-malfunction-status") {
                let code = latestMalfunction ? getCodeForLog(latestMalfunction.malfunction, latestMalfunction.status) : null;
                entity.value = code ? `1 (Code ${ code })` : "0";
            }
        }

        return headerData;
    }

    private renderReportNavigation() {
        const container = document.getElementById("datepicker-container");
        if (!container) {
            return;
        }

        if (this.dateSelectorReactRoot) {
            this.dateSelectorReactRoot.unmount();
        }

        const minDate = this.hosUtils.getRetentionStartDate(1);
        const maxDate = getStartOfDayUserTimezone(getServerDate());

        this.dateSelectorReactRoot = createRoot(container);
        this.dateSelectorReactRoot.render(
            React.createElement(
                LanguageProvider,
                { language: this.language as TSupportedLanguage },
                React.createElement(DateSelector, {
                    callback: (newDate: Date) => {
                        this.reportDate = newDate;
                        this.render(false);
                    },
                    minDate,
                    maxDate,
                    initialDate: this.reportDate
                })
            )
        );
    }

    private renderHeaderData(headerData, isMissingData) {
        void this.display(
            this.templateHeader({
                header: headerData,
                isMissingData: isMissingData,
                escapeValue: value => ((`${ value }`) || "&nbsp;").replace(/</g, "&lt;").replace(/>/g, "&gt;")
            }),
            this.language,
            this.el.querySelector("#report_dailyHeader") as HTMLElement
        );
    }

    private renderGraph() {
        this.graphView && this.display(this.templateGraph({}), this.language, this.el.querySelector("#hosReportGraph") as HTMLElement)
            .then(() => {
                this.graphView && this.graphView.setElement(document.getElementById("graph-inner-container")).render();
            });
    }

    private renderLogs(reportPeriodLogs: IReportPeriodLogs) {
        this.loadLogsPromise = this.display(this.templateLogs(), this.language, this.el.querySelector("#hosReportAllLogsContainer") as HTMLElement)
        .then(() => {
            let mainLogsElement = this.el.querySelector("#report_dailyDetails");
            reportPeriodLogs.driverLogs.length && this.addRotateSuggestion(mainLogsElement);
            void this.renderTableLogs(reportPeriodLogs.driverLogs, "#report_dailyDetails", null, undefined, this.language);

            let unassignedLogsElement = this.el.querySelector("#unassignedLogsReport_dailyDetails");
            reportPeriodLogs.unassignedLogs.length && this.addRotateSuggestion(unassignedLogsElement);
            void this.renderTableLogs(reportPeriodLogs.unassignedLogs, "#unassignedLogsReport_dailyDetails", null, undefined, this.language);

            let malfunctionLogsElement = this.el.querySelector("#malfunctions_dailyDetails");
            reportPeriodLogs.malfunctionLogs.length && this.addRotateSuggestion(malfunctionLogsElement);
            void this.renderTableLogs(reportPeriodLogs.malfunctionLogs, "#malfunctions_dailyDetails", null, undefined, this.language);
        });

        this.listenToOnce(dispatcher, DispatcherEvents.SyncDriverChange, () => {
            void this.loadData();
        });

        return this.loadLogsPromise;
    }

    private renderLogsCanadaELD(reportPeriodLogs: IReportPeriodLogs) {
        let currentUser = this.currentUser;
        this.loadLogsPromise = this.display(this.canELDTemplateLogs(), this.language, this.el.querySelector("#hosReportAllLogsContainer") as HTMLElement)
        .then(() => {
            let driverLogsElement = this.el.querySelector("#driver_dailyDetails");
            reportPeriodLogs.driverLogs.length && this.addRotateSuggestion(driverLogsElement);
            void this.renderTableLogs(reportPeriodLogs.driverLogs, "#driver_dailyDetails", currentUser, TableType.Driver, this.language);

            let loginLogoutCertificationMalfunctionLogsElement = this.el.querySelector("#loginLogoutCertificationMalfunction_dailyDetails");

            if (reportPeriodLogs.loginLogoutCertificationMalfunctionLogs) {
                reportPeriodLogs.loginLogoutCertificationMalfunctionLogs.length && this.addRotateSuggestion(loginLogoutCertificationMalfunctionLogsElement);
                void this.renderTableLogs(
                    reportPeriodLogs.loginLogoutCertificationMalfunctionLogs,
                    "#loginLogoutCertificationMalfunction_dailyDetails",
                    currentUser,
                    TableType.LoginLogoutCertificationMalfunction,
                    this.language
                );
            }

            if (reportPeriodLogs.cycleOperatingZoneDeferralLogs) {
                let cycleOperatingZoneDeferralLogsElement = this.el.querySelector("#cycleOperatingZoneDeferral_dailyDetails");
                reportPeriodLogs.cycleOperatingZoneDeferralLogs.length && this.addRotateSuggestion(cycleOperatingZoneDeferralLogsElement);
                void this.renderTableLogs(
                    reportPeriodLogs.cycleOperatingZoneDeferralLogs,
                    "#cycleOperatingZoneDeferral_dailyDetails",
                    currentUser,
                    TableType.CycleOperatingZoneDeferral,
                    this.language
                );
            }

            if (reportPeriodLogs.commentRemarkAnnotationLogs) {
                let commentRemarkAnnotationLogsElement = this.el.querySelector("#commentRemarkAnnotation_dailyDetails");
                reportPeriodLogs.commentRemarkAnnotationLogs.length && this.addRotateSuggestion(commentRemarkAnnotationLogsElement);
                void this.renderTableLogs(
                    reportPeriodLogs.commentRemarkAnnotationLogs,
                    "#commentRemarkAnnotation_dailyDetails",
                    currentUser,
                    TableType.CommentRemarkAnnotation,
                    this.language
                );
            }

            if (reportPeriodLogs.addHoursNotRecordedLogs) {
                let addHoursNotRecordedLogsElement = this.el.querySelector("#addHoursNotRecorded_dailyDetails");
                reportPeriodLogs.addHoursNotRecordedLogs.length && this.addRotateSuggestion(addHoursNotRecordedLogsElement);
                void this.renderTableLogs(
                    reportPeriodLogs.addHoursNotRecordedLogs,
                    "#addHoursNotRecorded_dailyDetails",
                    currentUser,
                    TableType.AddHoursNotRecorded,
                    this.language
                );
            }

            if (reportPeriodLogs.powerUpAndShutDownLogs) {
                let powerUpAndShutDownLogsElement = this.el.querySelector("#powerUpAndShutDown_dailyDetails");
                reportPeriodLogs.powerUpAndShutDownLogs.length && this.addRotateSuggestion(powerUpAndShutDownLogsElement);
                void this.renderTableLogs(
                    reportPeriodLogs.powerUpAndShutDownLogs,
                    "#powerUpAndShutDown_dailyDetails",
                    currentUser,
                    TableType.PowerUpAndShutDown,
                    this.language
                );
            }

            let unassignedLogsElement = this.el.querySelector("#unassigned_dailyDetails");
            reportPeriodLogs.unassignedLogs.length && this.addRotateSuggestion(unassignedLogsElement);
            void this.renderTableLogs(
                reportPeriodLogs.unassignedLogs,
                "#unassigned_dailyDetails",
                currentUser,
                TableType.Unassigned,
                this.language
            );
        });

        this.listenToOnce(dispatcher, DispatcherEvents.SyncDriverChange, () => {
            void this.loadData();
        });

        return this.loadLogsPromise;
    }

    private renderOdometer() {
        const odometerData = getOdometer(this.reportDate, this.app.localizerService, this.app.store, this.currentUser?.id);

        if (!odometerData || odometerData.length === 0) {
            const container = this.el.querySelector("#hosReportOdometer") as HTMLElement;
            if (container) {
                container.style.display = "none";
                container.innerHTML = "";
            }
            return Promise.resolve();
        }
        this.loadOdometerPromise = this.display(this.templateOdometer(), this.language, this.el.querySelector("#hosReportOdometer") as HTMLElement)
            .then(() => {
                void this.renderOdometerTable("#report_odometer", odometerData, this.language);
            });

        return this.loadOdometerPromise;
    }

    private renderOdometerTable(selector, odometerData: IOdometerRecord[], language?: string) {
        let container: HTMLElement = this.el.querySelector(selector);
        let getOdometerTableTemplate = (data: IOdometerRecord[]) => {
            return this.templateOdometerTable({
                odometerData: data
            });
        };

        return this.display(getOdometerTableTemplate(odometerData), language, container);
    }

    private addRotateSuggestion(anchorElement: HTMLElement) {
        let suggestionElement = createElement("div", "device-rotate-suggestion");
        // eslint-disable-next-line max-len
        suggestionElement.innerHTML = `<svg class="svgIcon" role="img" aria-label="Rotate suggestion icon"><use xlink:href="#drive-arrows-cw"></use></svg>${ this.getString("Rotate your device or swipe the table below for more details") }`;

        let parent = <HTMLElement>anchorElement.parentNode;
        parent.insertBefore(suggestionElement, anchorElement);
    }

    private renderTableLogs (tableLogs: IHosReportLog[], selector, currentUser?: DeepReadonly<IUser> | null, tableType?: TableType, language?: string) {
        let container: HTMLElement = this.el.querySelector(selector);
        let timeFormat = this.isCanadaEld ? "HH:mm:ss" : "HH:mm";
        let getLogsTableTemplate = (logs: IHosReportLog[]) => {
            let argsForTable = {
                formatDateTime: dateTime => `${ dayjs(dstCalculator.toLocalDateTime(dateTime)).format("MM-DD-YY") } ${ dayjs(dstCalculator.toLocalDateTime(dateTime)).format("HH:mm") }`,
                formatTime: dateTime => dayjs(dstCalculator.toLocalDateTime(dateTime)).format(timeFormat),
                formatDate: dateTime => dayjs(dstCalculator.toLocalDateTime(dateTime)).format("MM-DD-YY"),
                formatLogLocation: (log: IHosReportLog): string => {
                    return (!log.malfunction || (log.malfunction.indexOf("Malfunction") === -1 && log.malfunction !== "Diagnostic"))
                        && typeof log?.location === "string"
                        ? log.location
                        : "";
                },
                formatLogLatitudeLongitude: (log: IHosReportLog, coordinate?: number): string => {
                    switch (log.malfunction) {
                        case "Diagnostic":
                            return "X";
                        case "DiagnosticManualPosition":
                            return "M";
                        case "Malfunction":
                        case "MalfunctionManualPosition":
                            return "E";
                        default: {
                            // If no coordinate and no location, this indicates a missing location scenario
                            if (!coordinate && !log.location) {
                                return "X";
                            }
                            return coordinate ? (this.isCanadaEld ? coordinate?.toFixed(2) : coordinate?.toString()) : (log.location ? "M" : "");
                        }
                    }
                },
                formatOdometer: (log: IDutyStatusLogForReport, isCanada?: boolean) => {
                    const odometer = log.odometer;
                    const isCanadaCertifyLog = isCanada && log.status === "Certify";
                    return !log.isCreatedWhileDeviceNotConnected && typeof (odometer) !== "undefined" && !isCanadaCertifyLog ? this.app.localizerService.toLocalDistanceString(odometer) : "";
                },
                formatEngineHours: (log: IDutyStatusLogForReport, isAccumulated?: boolean, isCanada?: boolean) => {
                    if ((isCanada && log.status === "Certify") || log.isCreatedWhileDeviceNotConnected) {
                        return "";
                    }
                    const engineHours = isAccumulated ? log.engineHoursSinceLastPowerUp : log.engineHours;
                    let convertedHours = typeof (engineHours) !== "undefined" && engineHours > 0 ? engineHours / 60 / 60 : 0;
                    let adjustedHours = isAccumulated ? (convertedHours > 99.9 ? 99.9 : convertedHours) : (convertedHours > 99999.9 ? 99999.9 : convertedHours);
                    return `${ adjustedHours.toFixed(1) }${ this.getString("h") }`;
                },
                formatType: type => type === "INT_D" || type === "INT_PC" || type === "INT_CoDriver" ? this.getString("Int Location") : type,
                formatDistanceSinceLastValidOdometer: (distanceSince?: number) => {
                    const maxDistance = currentUser?.isMetric ? 9 : 6;
                    const distance = !distanceSince || distanceSince < 0 ? 0 : this.app.localizerService.toLocalDistance(distanceSince, true);
                    return `${ Math.floor(distance > maxDistance ? maxDistance : distance) }${ this.app.localizerService.getDistanceUnit(true) }`;
                },
                formatDistanceSinceLastPowerUp: (log: IDutyStatusLogForReport) => {
                    if (log.isCreatedWhileDeviceNotConnected) {
                        return "";
                    }

                    const distanceVal = log.distanceSinceLastPowerUp;
                    const distance = Math.min(9999, (!distanceVal || distanceVal < 0) ? 0 : Math.floor(this.app.localizerService.toLocalDistance(distanceVal, true)));
                    return `${ distance }${ this.app.localizerService.getDistanceUnit(true) }`;
                },
                formatRecordStatus: (status?: DutyStatusLogState) => {
                    return status ? EventRecordStatusParameterCoding[status] : "";
                },
                formatRecordOrigin: (origin?: DutyStatusLogOrigin) => {
                    return origin ? EventRecordOriginParameterCoding[origin] : "";
                },
                formatUserName: (userId?: string) => {
                    if (userId && userId !== NOUSERID) {
                        let user = this.userCache.get({ id: userId })[0];
                        return user?.name || "System";
                    }
                    return "System";
                },
                autoAnnotationLabel: AUTO_ANNOTATION_LABEL,
                logs
            };

            if (this.isCanadaEld) {
                switch (tableType) {
                    case TableType.LoginLogoutCertificationMalfunction:
                        return this.templateLoginLogoutCertificationMalfunctionLogsTable(argsForTable);
                    case TableType.CycleOperatingZoneDeferral:
                        return this.templateCycleOperatingZoneDeferralLogsTable(argsForTable);
                    case TableType.CommentRemarkAnnotation:
                        return this.templateCommentRemarkAnnotationLogsTable(argsForTable);
                    case TableType.AddHoursNotRecorded:
                        return this.templateAddHoursNotRecordedLogsTable(argsForTable);
                    case TableType.PowerUpAndShutDown:
                        return this.templatePowerUpAndShutDownLogsTable(argsForTable);
                    case TableType.Driver:
                    default:
                        return this.templateDriverLogsTable(argsForTable);
                }
            } else {
                return this.templateLogsTable(argsForTable);
            }
        };

        return this.display(getLogsTableTemplate(tableLogs), language, container);
    }

    private populateLogs(api: Api, logs: DutyStatusLog[]) {
        let isLocationAvailable = (log: DutyStatusLog) => {
            return !!log.location && !(log.location?.address?.formattedAddress);
        };

        let addressLookups: IAddressSearch[] = logs.filter(log => isLocationAvailable(log)).map((log) => {
            return {
                id: log.id,
                coordinate: <ICoordinate>(<IAddressLookupData>log.location).location
            };
        });

        return populateAddressesRedux(api, this.app.store, addressLookups, false)
            .then((addresses) => {
                let getKey = (coordinate: ICoordinate) => `${ coordinate.x }__${ coordinate.y }`;
                let locationsHash = addresses.reduce((hash, location, index) => {
                    let params = addressLookups[index];
                    if (params && location) {
                        hash[getKey(params.coordinate)] = location && location.formattedHosAddress ? location.formattedHosAddress : "";
                    }
                    return hash;
                }, [] as string[]);

                logs.filter(log => isLocationAvailable(log)).forEach((log) => {
                    let key = getKey(<ICoordinate>(<IAddressLookupData>log.location).location);
                    if (locationsHash[key]) {
                        (<IAddressLookupData>log.location).address = log.location?.address || {};
                        log.location?.address && (log.location.address.formattedAddress = locationsHash[key]);
                    }
                });

                return logs;
            });
    }

    private getTimeZoneOffset(timeZoneId?: string) {
        let refDate = this.reportDate || getServerDate();

        if (timeZoneId && dstCalculator.hasTimeZoneInfo(timeZoneId)) {
            let offset = dstCalculator.getOffset(refDate, timeZoneId) - refDate.getTimezoneOffset();
            return `${ `0${ Math.floor(offset / 60) }`.slice(-2) }:${ `0${ Math.floor(offset % 60) }`.slice(-2) }`;
        }

        return "00:00";
    }

    private getAddressByLatLng(latitude, longitude) {
        return populateAddressesRedux(this.api, this.app.store, [{
            id: `${ longitude }_${ latitude }`,
            coordinate: {
                x: longitude,
                y: latitude
            }
        }], false)
        .then(([address]) => {
            let newAddress = address && address.formattedHosAddress ? address.formattedHosAddress : "";
            if (this.isCanadaEld) {
                newAddress += (address ? ", " : "") + `${ latitude.toFixed(2) }, ${ longitude.toFixed(2) }`;
            }
            return newAddress;
        })
        .catch(() => "");
    }

    private getPositionByDate(vehicle, date) {
        return new Promise<any>((resolve) => {
            if (!vehicle || !vehicle.id || vehicle.id === "NoDeviceId") {
                resolve(null);
                return;
            }

            this.api.call(
                "Get",
                {
                    typeName: "LogRecord",
                    search: {
                        fromDate: date,
                        toDate: date,
                        deviceSearch: {id: vehicle.id}
                    },
                    resultsLimit: 1
                },
                locations => {
                    let location = locations[0] || null;

                    resolve(location);
                },
                function() {
                    resolve(null);
                }
            );
        });
    }

    private getCurrentAddress(vehicle) {
        return getCurrentPosition(this.app.store)
            .then<string>((position: GeolocationPosition) => this.getAddressByLatLng(position.coords.latitude, position.coords.longitude))
            .catch(() => {
                let currentDate = dstCalculator.toReverseOffsetDateTime(getServerDate());
                return this.getAddressByDate(vehicle, currentDate);
            });
    }

    private getAddressByDate (vehicle, date) {
        return this.getPositionByDate(vehicle, date)
            .then(location => location ? this.getAddressByLatLng(location.latitude, location.longitude) : "");
    }

    private formatDateAndTime(date) {
        return dayjs(date).format("MM-DD-YY HH:mm:ss");
    }

    private formatDate(date) {
        return dayjs(date).format("MM-DD-YY");
    }

    private getDriverName(driver: IUser) {
        return this.joinValues(driver.lastName || "", driver.firstName || "");
    }

    private joinValues(...names: string[]) {
        return [].filter.call(names, value => !!value).join(", ");
    }

    private getTrailerNames(reportDate: Date): string[] {
        let apiUser = this.api.getUser();
        let trailerAttachmentsData = apiUser ? (this.app.store.getState().trailerAttachments[apiUser.id] || {}) : {};
        let trailersData = this.trailerCache.getDict(apiUser?.id);
        let reportDateTime = reportDate.getTime();
        let currentVehicle = getCurrentVehicle();

        return Object.keys(trailerAttachmentsData).reduce((attachments: string[], trailerAttachmentId) => {
            let trailerAttachment = trailerAttachmentsData[trailerAttachmentId];
            let activeFrom = trailerAttachment.activeFrom && new Date(trailerAttachment.activeFrom).getTime();
            let activeTo = trailerAttachment.activeTo && new Date(trailerAttachment.activeTo).getTime();

            if (currentVehicle && currentVehicle.id === trailerAttachment.device?.id && activeFrom && activeFrom <= reportDateTime && (!activeTo || activeTo >= reportDateTime)) {
                let trailerId = trailerAttachment.trailer?.id;
                let name = trailerId && trailersData[trailerId] && trailersData[trailerId].name;
                name && attachments.push(name);
            }

            return attachments;
        }, []).sort();
    }

    private getLogsInfo(): [string, boolean] {
        let distanceToday = "";
        let isInCanadaWithPC = false;
        let logs = this.reportPeriodStandardLogs;
        let milesTotal = 0;

        logs.forEach(log => {
            [isInCanadaWithPC, milesTotal] = addLogDistance(log, milesTotal, this.hosUtils, isInCanadaWithPC);
        });
        distanceToday = this.app.localizerService.toLocalDistanceString(milesTotal);

        return [distanceToday, isInCanadaWithPC];
    }

    private getDailyDistanceByDevice(dailyMetresByDevice: IIdDictionary<IDistanceDisplay>) {
        let logs = this.reportPeriodStandardLogs;
        let addToDictionary = (vehicleId: string, distance: number) => {
            if (!dailyMetresByDevice.hasOwnProperty(vehicleId)) {
                dailyMetresByDevice[vehicleId] = { distance: 0 };
            }
            dailyMetresByDevice[vehicleId].distance += distance;
        };

        logs.forEach(log => {
            if (log.getDeviceId() === "NoDeviceId") {
                return;
            }

            if (log.isPCLog()) {
                let ruleSetBeforeLog = this.hosUtils.getUserHosRuleSets().getRulesetByDate(log.getDate());

                // PC distance do not count for canadian ELD
                if (!(ruleSetBeforeLog && isInCanada(ruleSetBeforeLog.hosRuleSet.id) && log.getDistance() > 0)) {
                    addToDictionary(log.device.id, log.getDistance());
                }
            } else {
                addToDictionary(log.device.id, log.getDistance());
            }
        });
    }

    private getCarrierName(driver: IUser) {
        return driver.carrierNumber || this.getString("No carrier number");
    }

    private splitComment(comment = "", previous: boolean): string {
        let bothAuth = comment.split(" changed to ");
        let authString = bothAuth[previous ? 0 : 1];
        let splited = bothAuth[1] ? authString.replace("/", ", ") : "";

        return splited;
    }

    private compareDate(bigger: Date, lower: string): boolean {
        let first = bigger;
        let second = new Date(lower);

        return first > second;
    }

    private getCompanyNameAddress (driver) {
        let companyName = driver.authorityName || "";
        let companyAddress = driver.authorityAddress || "";

        return this.joinValues(companyName, companyAddress);
    }

    private formatOdometer(odometer) {
        return this.app.localizerService.toLocalDistanceString(odometer);
    }

    private formatEngineHours(engineHours: number) {
        return `${ this.roundToOneDecimalPlace(engineHours / 60 / 60) } ${ this.getString("hours") }`;
    }

    private formatLicenseProvince(licenseProvince) {
        let alphabeticalLicenseProvince = licenseProvince.replace(/[^a-zA-Z]/gi, "");
        return alphabeticalLicenseProvince.slice(0, 2);
    }

    private formatLicenseNumber(licenseNumber) {
        let alphanumericLicenseNumber = licenseNumber.replace(/[^0-9a-zA-Z]/gi, "");
        return alphanumericLicenseNumber.slice(0, 20);
    }

    private getVehicle(deviceId?: string) {
        return deviceId
            ? this.deviceCache.getOrFetch(this.api, { id: deviceId }).catch(() => null)
            : Promise.resolve(null);
    }

    private populateOdoEngineHours(isCanEld: boolean, currentInfo: { odometer: string; engineHours: string }, header: IHeader[]) {
        let odometerLabel = isCanEld ? "Current Total Distance" : "Current odometer";
        header.push({ id: "eld-vehicle-odometer", label: odometerLabel, value: currentInfo.odometer });
        let engineHoursLabel = isCanEld ? "Current Total Engine Hours" : "Current Engine Hours";
        header.push({ id: "eld-vehicle-engine-hours", label: engineHoursLabel, value: currentInfo.engineHours });
    }

    private populateVehicleInformation(isCanEld: boolean, vehicle: Device | null, distanceDictionary: IIdDictionary<IDistanceDisplay>, header: IHeader[]) {
        if (!isCanEld) {
            header.push({ id: "eld-vehicle-serial", label: "Truck Tractor ID", value: vehicle && vehicle.name ? vehicle.name : this.getString("No") });
            header.push({ id: "eld-vehicle-vin", label: "Truck Tractor VIN", value: vehicle && vehicle.vehicleIdentificationNumber ? vehicle.vehicleIdentificationNumber : this.getString("No") });
            header.push({ id: "eld-vehicle-license-plate", label: "License Plate", value: vehicle && vehicle.licensePlate ? vehicle.licensePlate : this.getString("No") });
            header.push({ id: "eld-vehicle-license-state", label: "License Plate Jurisdiction", value: vehicle && vehicle.licenseState ? vehicle.licenseState : this.getString("No") });
            return;
        }

        if (Object.keys(distanceDictionary).length === 0) {
            header.push({ id: "eld-vehicle-serial", label: "Truck Tractor ID", value: this.getString("No") });
            header.push({ id: "eld-vehicle-vin", label: "Truck Tractor VIN", value: this.getString("No") });
            return;
        }

        let vehicleNames: string[] = [];
        let vehicleVins: string[] = [];
        let vehicleDistances: string[] = [];
        let vehicleLicensePlates: string[] = [];
        let vehicleLicenseStates: string[] = [];

        hashToArray(distanceDictionary).forEach((distanceDisplay, index) => {
            let elementNumber = index + 1;
            vehicleNames.push(`${ elementNumber }. ${ htmlEscape(distanceDisplay.device?.name || "") }`);
            vehicleVins.push(`${ elementNumber }. ${ htmlEscape(distanceDisplay.device?.vehicleIdentificationNumber || "") }`);
            vehicleLicensePlates.push(`${ elementNumber }. ${ htmlEscape(distanceDisplay.device?.licensePlate || "") }`);
            vehicleLicenseStates.push(`${ elementNumber }. ${ htmlEscape(distanceDisplay.device?.licenseState || "") }`);
            vehicleDistances.push(`${ elementNumber }. ` + this.app.localizerService.toLocalDistanceString(distanceDisplay.distance));
        });

        header.push({ id: "eld-vehicle-serial", label: "Truck Tractor ID", value: vehicleNames.join("<br>"), hasHtml: true });
        header.push({ id: "eld-vehicle-vin", label: "Truck Tractor VIN", value: vehicleVins.join("<br>"), hasHtml: true });
        header.push({ id: "eld-vehicle-license-plate", label: "License Plate", value: vehicleLicensePlates.join("<br>"), hasHtml: true });
        header.push({ id: "eld-vehicle-license-state", label: "License Plate Jurisdiction", value: vehicleLicenseStates.join("<br>"), hasHtml: true });
        header.push({ id: "eld-vehicle-distance-today", label: "Distance Today", value: vehicleDistances.join("<br>"), hasHtml: true });
    }

    private addCodriverData (headerData: IHeader[], codrivers: IUser[]) {
        if (this.isCanadaEld) {
            let codriverNames: string[] = [];
            let codriverIds: string[] = [];

            codrivers.forEach((codriver, index) => {
                let elementNumber = index + 1;
                codriverNames.push(`${ elementNumber }. ${ htmlEscape(this.getDriverName(codriver)) }`);
                codriverIds.push(`${ elementNumber }. ${ htmlEscape(codriver.name) }`);
            });
            headerData.push({ id: "eld-codriver-name", label: "Co-driver Name", value: codriverNames.join("<br>"), hasHtml: true });
            headerData.push({ id: "eld-codriver-id", label: "Co-driver ID", value: codriverIds.join("<br>"), hasHtml: true });
        } else {
            codrivers.forEach(codriver => {
                let codriverLicenseProvince = this.formatLicenseProvince(codriver && codriver.licenseProvince ? codriver.licenseProvince : "");
                let codriverLicenseNumber = this.formatLicenseNumber(codriver && codriver.licenseNumber ? codriver.licenseNumber : "");
                headerData.push({ id: "eld-first-codriver-name", label: "Co-driver Name", value: this.getDriverName(codriver) });
                headerData.push({ id: "eld-first-codriver-id", label: "Co-driver ID", value: codriver.name });
                codriverLicenseProvince && headerData.push({ id: "eld-first-codriver-license-state", label: "Co-driver License State", value: codriverLicenseProvince });
                codriverLicenseNumber && headerData.push({ id: "eld-first-codriver-license-number", label: "Co-driver License Number", value: codriverLicenseNumber });
            });
        }
    }

    private getLastLog() {
        let lastLog = this.reportPeriodStandardLogs.length ? this.reportPeriodStandardLogs[this.reportPeriodStandardLogs.length - 1] : null;
        if (lastLog === null || lastLog.getDeviceId() === NODEVICEID) {
            let logsWithDeviceId = this.standardLogsCollection.filter(log => log.device.id !== NODEVICEID);
            lastLog = logsWithDeviceId.length > 0 ? logsWithDeviceId[logsWithDeviceId.length - 1] : null;
        }
        return lastLog;
    }

    // eslint-disable-next-line complexity
    private getHeaderData(unassignedLogs) {
        let reportDate = this.reportDate;
        let reportPeriod = this.reportPeriod;
        let driver = this.currentUser;
        if (!driver) {
            this.app.session.close(null, true);
            return Promise.reject();
        }
        let hasUnassignedLogs = !!unassignedLogs.length;
        let userTimeZoneOffset = this.getTimeZoneOffset(driver.timeZoneId);
        let vehicle: Device | null = null;
        let coDrivers: IIdEntity[] = [];
        let eldInfo: IEldProviderInfo;
        let currentInfo = {
            odometer: this.formatOdometer(0),
            engineHours: this.formatEngineHours(0)
        };
        let distanceToday = "...";
        let isInCanadaWithPC = false;
        let distanceString = "Distance Today";
        let totalHoursInCycle = "...";
        let totalHoursRemainingInCycle = "...";
        let workShiftHours = "...";
        let hasExemptions = (/sales|shortHaul|carrierExemption/i.test(this.hosUtils.getLatestHosRuleSetIdShortened() || ""));
        let header: IHeader[] = [];
        let userLicenseProvince = this.formatLicenseProvince(driver.licenseProvince || "");
        let userLicenseNumber = this.formatLicenseNumber(driver.licenseNumber || "");
        let logDistanceDictionary: IIdDictionary<IDistanceDisplay> = this.getAllVehiclesUsedInRange();

        let getAnnotationsForAuthorityLogs = logs => {
            const apiUserId = this.api.getUser()?.id;
            const annotations = getClonedAnnotationsForUser(this.app.store, apiUserId);

            return logs.reduce((authAnns, log) => {
                let annId = log.annotations[0].id;

                if (annotations[annId]) {
                    authAnns.push(annotations[annId]);
                }

                return authAnns;
            }, []);
        };

        let getAuthorityForDate = (annotations) => {
            let midnight = new Date(reportDate.getTime()).setHours(0, 0, 0, 0);
            let nextDayStartDate = new Date(midnight + 1000 * 60 * 60 * 24);

            if (annotations.length) {
                // getting the latest authority annotation for this day
                for (let i = annotations.length - 1; i >= 0; i--) {
                    let annotation = annotations[i];

                    if (this.compareDate(nextDayStartDate, annotation.dateTime)) {
                        return this.splitComment(annotation.comment, false);
                    }
                }

                // there are no authority logs before this day
                let oldestAnnotation = annotations[0];
                return this.splitComment(oldestAnnotation.comment, true);
            }

            // there is no authority logs, getting authority from driver settings
            return this.getCompanyNameAddress(this.currentUser);
        };

        let authorityAnnotations = getAnnotationsForAuthorityLogs(this.authorityLogs);
        let currentDayAuthority = getAuthorityForDate(authorityAnnotations);

        // We need to look for all local logs if driver doesn't have standard logs for current reporting period.
        let lastLog = this.getLastLog();

        let deferralLogs = this.cycleOperatingZoneDeferralLogsCollection.filter(log => log.status === "ExemptionOffDutyDeferral");
        let latestDeferralLog = deferralLogs.length > 0 ? deferralLogs[deferralLogs.length - 1] : null;
        let lastLogDeferralStatus: DeferralStatus = latestDeferralLog && latestDeferralLog.deferralStatus ? DeferralStatus[latestDeferralLog.deferralStatus] : DeferralStatus["None"];
        let lastLogDeferralTimestamp: string | "00:00" = "00:00";

        if (latestDeferralLog && latestDeferralLog.deferralMinutes) {
            lastLogDeferralTimestamp = `${ `0${ Math.floor(latestDeferralLog.deferralMinutes / 60) }`.slice(-2) }:${ `0${ latestDeferralLog.deferralMinutes % 60 }`.slice(-2) }`;
        }

        let lastLogDate = getServerDate();
        let isLastDay = !this.hosUtils.canNavigateNextDay(reportPeriod.fromDate);
        let currentVehicle = getCurrentVehicle();
        let lastDeviceId: string | undefined = currentVehicle ? currentVehicle.id : "NoDeviceId";

        if (isLastDay) {
            // If there is no assigned vehicle for a driver when he/she goes into inspection mode,
            // just use the last used vehicle to populate the fields.
            if (lastLog && lastDeviceId === "NoDeviceId") {
                lastDeviceId = lastLog.getDeviceId();
                lastLogDate = lastLog.getDate();
            }
            coDrivers = this.app.userService.getCoDrivers(driver.name, true);
            this.coDriverPromise = Promise.resolve((coDrivers as IUser[]) || null);
        } else {
            this.coDriverPromise = Promise.resolve(null);
            if (lastLog) {
                lastDeviceId = lastLog.getDeviceId();
                lastLogDate = lastLog.getDate();
                coDrivers = lastLog.coDrivers || [];
                if (coDrivers.length) {
                    this.coDriverPromise = this.userCache.getOrFetch(this.api, coDrivers)
                        .then((result) => (result && result[0] && result[0]) || null)
                        .catch(() => null);
                }
            } else {
                lastLogDate = reportPeriod.toDate;
            }
        }

        let trailerNames = this.getTrailerNames(lastLogDate);
        let shipmentNames: string[] = [];
        let rulesetName = this.app.dutyStatusLogService.getRuleSetForDate(this.hosUtils, reportPeriod.toDate).replace("HosRuleSet", "");
        let displayedRuleset = this.hosUtils.getRuleSet(rulesetName);
        let calculationRuleset = getRuleSetForCalculation(
            rulesetName,
            this.featureFlagsService.hasFeatureFlag(FeatureFlagName.DISABLE_TEXAS_SHORT_HAUL_RULESET_CORRECTION)
        );
        let reportRuleset = calculationRuleset ?? displayedRuleset;

        // Get cycle maximum for driver's ruleset
        let rulesetMaximum = getDefaultAvailability(calculationRuleset ?? rulesetName).cycle;

        let ruleResult = this.hosUtils.getRuleResult(false, true, true);
        let startOfDay = getStartOfDay(dstCalculator.toLocalDateTime(this.reportDate));
        [totalHoursInCycle, totalHoursRemainingInCycle] =
        getCycleInfo(
            startOfDay,
            ruleResult?.daySummaries,
            rulesetMaximum
        );

        workShiftHours = getHoursInWorkShiftFromWorkdaySummary(ruleResult?.workdaySummaries || [], this.reportDate, this.reportPeriod.toDate);

        if (this.isCanadaEld) {
            this.getDailyDistanceByDevice(logDistanceDictionary);
            if (isLastDay && lastDeviceId && lastDeviceId !== "NoDeviceId" && !logDistanceDictionary.hasOwnProperty(lastDeviceId)) {
                logDistanceDictionary[lastDeviceId] = { distance: 0 };
            }
        } else {
            [distanceToday, isInCanadaWithPC] = this.getLogsInfo();
            if (isInCanadaWithPC) {
                distanceString = "Distance Today (excl. PC)";
            }
        }

        let deviceIds = Object.keys(logDistanceDictionary);
        return Promise.all([
            Promise.all(deviceIds.map(deviceId => this.getVehicle(deviceId))),
            this.getVehicle(lastDeviceId),
            this.requestEldAuthenticationValue(lastDeviceId),
            fetchMissingShipments(this.api, this.app.store, deviceIds, this.reportPeriod)
        ])
        .then(([allDevices, lastDevice, _1, fetchedShipments]) => {
            allDevices.forEach(deviceCacheLookup => {
                if (deviceCacheLookup && deviceCacheLookup[0] && deviceCacheLookup[0][0]) {
                    let device = deviceCacheLookup[0][0];
                    if (logDistanceDictionary[device.id]) {
                        logDistanceDictionary[device.id].device = device;
                    }
                }
            });
            vehicle = lastDevice ? new Device(lastDevice[0][0]) : null;

            if (vehicle) {
                this.geoLocationPromise = !this.currentAddress ? this.getCurrentAddress(vehicle) : Promise.resolve(this.currentAddress);
            }
            allDevices?.forEach(deviceArray => {
                deviceArray?.forEach((device: Device) => {
                    shipmentNames.push(...getShipmentNamesForVehicle(this.app.store, lastLogDate, new Device(device[0]).id, fetchedShipments));
                });
            });
            eldInfo = this.getEldProviderInfo(vehicle);

            if (isLastDay && vehicle && lastDeviceId !== "NoDeviceId") {
                let dailyStatusInfo = this.app.dutyStatusLogService.getDailyStatusInfo(dstCalculator.toLocalDateTime(reportDate), vehicle.id, this.app.store);

                if (dailyStatusInfo) {
                    currentInfo.odometer = this.formatOdometer(dailyStatusInfo.end);
                    currentInfo.engineHours = this.formatEngineHours(dailyStatusInfo.endEngineHours);
                }
            } else if (lastLog) {
                currentInfo.odometer = this.formatOdometer(lastLog.odometer || 0);
                currentInfo.engineHours = this.formatEngineHours(lastLog.engineHours || 0);
            }

            return Promise.all([this.coDriverPromise, this.geoLocationPromise]);
        })
        .then(([coDriver, geoLocation]) => {
            this.currentAddress = geoLocation;
            let isCanEld = this.isCanadaEld;

            // General Header
            header.push({ id: "eld-record-date", label: "Record Date", value: this.formatDate(dstCalculator.toLocalDateTime(reportPeriod.fromDate)) });
            header.push({ id: "eld-current-date", label: "Current Date and Time", value: this.formatDateAndTime(dstCalculator.toLocalDateTime(this.currentDateTimeOfPrintoutDisplay)) });
            if (isCanEld) {
                let startingTime = this.app.dutyStatusLogService.getRuleSetStartOfDayForDate(this.hosUtils, reportPeriod.toDate);
                if (startingTime) {
                    header.push({
                        id: "eld-day-starting-time",
                        label: "Day Starting Time",
                        value: startingTime
                    });
                }
            }
            header.push({ id: "eld-utc-offset", label: "UTC Timezone Offset", value: userTimeZoneOffset });
            header.push({ id: "eld-carrier", label: "Carrier", value: this.getCarrierName(driver as IUser) });

            if (eldInfo) {
                header.push({ id: "eld-info-id", label: "ELD ID", value: eldInfo.id });
                header.push({ id: "eld-info-provider", label: "ELD Provider", value: eldInfo.provider });

                if (isCanEld) {
                    header.push({
                        id: "eld-authentication-value",
                        label: "ELD Authentication",
                        value: this.eldAuthenticationValue
                    });

                    let credentials = this.app.api.getCurrentCredentials();
                    let database = credentials ? credentials.credentials.database : "";
                    header.push({
                        id: "eld-registration-id",
                        label: "ELD Certification",
                        value: getCanadaRegistrationId(
                            getEldProviderName(database, window.whitelabel, isCanEld),
                            isIphone() ? CanadaEldOperatingSystems.IOS : CanadaEldOperatingSystems.Android,
                            vehicle
                        )
                    });
                }
            }

            // Driver Header
            header.push({ id: "eld-driver-name", label: "Driver Name", value: this.getDriverName(driver as IUser) });
            header.push({ id: "eld-driver-id", label: "Driver ID", value: (driver as IUser).name });

            userLicenseProvince && header.push({ id: "eld-driver-license-state", label: "Driver License Jurisdiction", value: userLicenseProvince });
            header.push({ id: "eld-driver-ruleset", label: "Ruleset", value: reportRuleset.name, translate: true });
            userLicenseNumber && header.push({ id: "eld-driver-license-number", label: "Driver License Number", value: userLicenseNumber });

            if (coDriver && coDriver.length > 0) {
                this.addCodriverData(header, coDriver);
            } else {
                header.push({ id: "eld-first-codriver-name", label: "Co-driver Name", value: this.getString("No") });
            }

            // Assets Header
            this.populateOdoEngineHours(isCanEld, currentInfo, header);
            this.populateVehicleInformation(isCanEld, vehicle, logDistanceDictionary, header);

            header.push({ id: "eld-shipment-id", label: "Shipping ID", value: shipmentNames.join(", ") || this.getString("No") });
            header.push({ id: "eld-trailer-id", label: "Trailer ID", value: trailerNames.join(", ") || this.getString("No") });
            header.push({ id: "eld-current-location", label: "Current Geolocation", value: this.currentAddress || "" });
            header.push({ id: "eld-status-exemptions", label: "Exempt Driver Status", value: hasExemptions ? this.getString("Yes") : this.getString("No") });

            if (isCanEld) {
                header.push({ id: "eld-driver-cycle-days", label: "Cycle", value: reportRuleset.config.cycle?.cycleDays?.toString() });
                header.push({ id: "eld-driver-operating-zone", label: "Operating Zone", value: getOperatingZoneFromRuleSet(displayedRuleset.id) });

                header.push({ id: "eld-status-unidentified", label: "Unidentified Driving Records", value: hasUnassignedLogs ? "1" : "0" });
                header.push({ id: "eld-status-data-diagnostic-status", label: "Data Diagnostic Status (Status and Diagnostic Code)", value: "" });
                header.push({ id: "eld-status-malfunction-status", label: "Malfunction Status (Status and Malfunction Code)", value: "" });

                header.push({ id: "eld-status-total-hours-in-work", label: "Total Hours in Work Shift", value: workShiftHours });
                header.push({ id: "eld-status-total-hours-in-cycle", label: "Total Hours in Cycle", value: totalHoursInCycle });
                header.push({ id: "eld-status-remaining-hours-in-cycle", label: "Remaining hours in cycle", value: totalHoursRemainingInCycle });

                header.push(
                    {
                        id: "eld-status-off-duty-time-deferral",
                        label: "Off-Duty Time Deferral (Status and Time Deferred)",
                        value: `${ lastLogDeferralStatus } (${ lastLogDeferralTimestamp })`
                    }
                );
            } else {
                header.push({ id: "eld-status-unidentified", label: "Unidentified Driving Records", value: hasUnassignedLogs ? this.getString("Yes") : this.getString("No") });
                header.push({ id: "eld-status-malfunctions", label: "ELD Malfunction Indicators", value: "" });
                header.push({ id: "eld-status-diagnostics", label: "Driver’s Data Diagnostic Status", value: "" });
                header.push({ id: "eld-status-distance-today", label: distanceString, value: distanceToday });
            }

            header.push({ id: "company-name-address", label: isCanEld ? "Carrier Name/Address" : "Company Name/Address", value: currentDayAuthority });
            header.push({
                id: "home-terminal-name-address",
                label: "Home Terminal/Home Terminal Address",
                value: this.joinValues(
                    (driver as IUser).companyName || "",
                    (driver as IUser).companyAddress || ""
                )
            });

            return (header);
        })
        .catch(handleApi);
    }

    private cancelPromises() {
        this.renderPromise && this.renderPromise.cancel();
        this.geoLocationPromise && this.geoLocationPromise.cancel();
        this.coDriverPromise && this.coDriverPromise.cancel();
        this.loadLogsPromise && this.loadLogsPromise.cancel();
        this.loadOdometerPromise && this.loadOdometerPromise.cancel();
    }

    // fix eslint complexity issue
    private getProvider(label: string | null) {
        switch (label) {
            case "spireon":
                return {
                    id: "",
                    provider: "Spireon, Inc."
                };
            case "transflo":
                return {
                    id: this.isCanadaEld ? (isIphone() ? "TFM002" : "TFM001") : "TFMELD",
                    provider: "Pegasus Transtech"
                };
            case "smartdrive":
                return {
                    id: "SDSISR",
                    provider: "SmartDrive Systems"
                };
            case "eleos":
                return {
                    id: "ELEOS1",
                    provider: "Eleos Technologies"
                };
            case "lytx":
                return {
                    id: "LYTX01",
                    provider: "Lytx, Inc."
                };
            case "shell":
                return {
                    id: "SHELL9",
                    provider: "Shell Fleet Solutions"
                };
            default:
                return {};
        }
    }

    private getEldProviderInfo(device: Device | null): IEldProviderInfo {
        if (device && device.productId) {
            switch (device.productId) {
                case 10042: // D8XXXXX
                    return {
                        id: "VOLVO1",
                        provider: "Geotab Inc."
                    };
                case 10068: // DYXXXXX
                    return {
                        id: "MACK01",
                        provider: "Geotab Inc."
                    };
                case 10070: // E1XXXXX
                    return {
                        id: "NVSRN2",
                        provider: "Geotab Inc."
                    };
                case 10122: // FIXXXXX
                    return {
                        id: "LYTX01",
                        provider: "Lytx, Inc."
                    };
                case 10066: // DWXXXXX
                case 10080: // EBXXXXX
                    return {
                        id: "FORD01",
                        provider: "Geotab Inc."
                    };
                case 10163: // Netradyne D430 - HOXXXXX
                    return {
                        id: "DRI430",
                        provider: "Netradyne"
                    };
                case 10156: // Netradyne D210 - HHXXXXX
                    return {
                        id: "DRI210",
                        provider: "Netradyne"
                    };
                case 10178: // Netradyne D215 - I4XXXXX
                    return {
                        id: "DRI215",
                        provider: "Netradyne"
                    };
                case 10362: // Netradyne D450 - NDXXXXX
                    return {
                        id: "DRI450",
                        provider: "Netradyne"
                    };
                case 10067: // Daimler - DXXXXXX
                    return {
                        id: "DAIM01",
                        provider: "Geotab Inc."
                    };
                default:
                    break;
            }
        }
        let providerInfo = this.getProvider(window.whitelabel);
        if (providerInfo.id) {
            return providerInfo;
        }

        if (this.isCanadaEld && (device?.deviceType === "GO10" || device?.deviceType === "GO10Lite")) {
            return isIphone()
                ? { id: "GOELD3", provider: "Geotab Inc." }
                : { id: "GOELD4", provider: "Geotab Inc." };
        }

        const certifiedGoDevices: TDeviceType[] = ["GO7", "GO8", "GO9", "GO9B"];
        if (this.isCanadaEld && certifiedGoDevices.some(deviceType => device?.deviceType === deviceType)) {
            return isIphone()
                ? { id: "GOELD2", provider: "Geotab Inc." }
                : { id: "GOELD1", provider: "Geotab Inc." };
        }

        return { id: "GEOTAB", provider: "Geotab Inc." };
    }

    private roundToOneDecimalPlace(toRound: number): string {
        return (Math.round(toRound * 10) / 10).toFixed(1);
    }
}

